import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BoardEntry, DiscoverEntry, EmptyBoard, MockBoardList } from "@/components/board";
import { BoardOpeningBanner } from "@/components/board-opening-banner";
import { BoardHistoryLinks } from "@/components/history/board-history-links";
import {
  categoriesQuery,
  categoryBoardQuery,
  categoryBySlugQuery,
  discoverListingsQuery,
  boardScopeIds,
} from "@/lib/queries";
import { buildPageMetadata, SITE_URL } from "@/lib/seo";
import { formatUsd } from "@/lib/behot";
import { useT } from "@/i18n";

export const Route = createFileRoute("/c/$slug")({
  loader: ({ params, context }) =>
    context.queryClient.ensureQueryData(categoryBySlugQuery(params.slug)),
  head: ({ params, loaderData }) => {
    const category = loaderData ?? null;
    const label =
      category?.name ??
      params.slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    if (!category) {
      return buildPageMetadata({
        path: `/c/${params.slug}`,
        title: `${label} | BeHot.Now`,
        description: "This board is not available on BeHot.Now.",
        noindex: true,
      });
    }
    return buildPageMetadata({
      path: `/c/${category.slug}`,
      title: `Top ${label} Right Now — Live Rankings | BeHot.Now`,
      description:
        category.description ||
        `Discover the hottest ${label} right now on BeHot.Now. See live rankings, biggest movers, profiles and who's gaining attention today.`,
      ogTitle: `Hottest ${label} right now`,
      ogDescription: `Live ${label} rankings, biggest movers and who's gaining attention today on BeHot.Now.`,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Categories", path: "/categories" },
        { name: label, path: `/c/${category.slug}` },
      ],
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: `Top ${label} right now`,
        url: `${SITE_URL}/c/${category.slug}`,
        description:
          category.description || `Live BeHot.Now ranking board for ${label}.`,
      },
    });
  },
  component: CategoryPage,
});

function CategoryPage() {
  const { t } = useT();
  const { slug } = Route.useParams();
  const { data: category, isLoading: loadingCategory } = useQuery(categoryBySlugQuery(slug));
  const { data: categories = [] } = useQuery(categoriesQuery);
  const scope = boardScopeIds(categories, category?.id);
  const { data: board = [], isLoading } = useQuery(categoryBoardQuery(scope));
  const { data: discover = [] } = useQuery(discoverListingsQuery(scope));

  const children = categories.filter((c) => c.parent_id === category?.id);
  const boardIds = new Set(board.map((r) => r.listing.id));
  const discoverOnly = discover.filter((l) => !boardIds.has(l.id)).slice(0, 8);

  if (!loadingCategory && !category) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-bold">{t("boards.category.notFound")}</h1>
        <Button asChild variant="hot" className="mt-4">
          <Link to="/hot">{t("boards.category.browseAll")}</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <h1 className="font-display text-3xl font-bold sm:text-4xl">
        {category?.name ?? t("boards.category.loading")}
      </h1>
      {category?.description && (
        <p className="mt-2 max-w-lg text-muted-foreground">{category.description}</p>
      )}
      {category && (
        <div className="mt-4 grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-2 text-sm text-muted-foreground sm:flex sm:flex-wrap sm:items-center">
          <Flame className="mt-0.5 size-4 shrink-0 text-primary sm:mt-0" />
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span>{t("boards.category.minimumBoost")}</span>
          {board.length === 0 && category.opening_boost_cents < category.minimum_boost_cents ? (
            <>
              <span className="line-through opacity-60">
                {formatUsd(category.minimum_boost_cents)}
              </span>
              <strong className="text-foreground">{formatUsd(category.opening_boost_cents)}</strong>
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                {t("boards.category.openingOffer")}
              </span>
            </>
          ) : (
            formatUsd(category.minimum_boost_cents)
          )}
          <span className="whitespace-nowrap">· {t("boards.category.dayDuration", { n: category.boost_duration_days })}</span>
          </div>
        </div>
      )}


      {category && <BoardOpeningBanner category={category} isEmpty={!isLoading && board.length === 0} />}

      <div className="mt-5 flex flex-wrap gap-3">
        <Button asChild variant="hot">
          <Link to="/get-hot" search={{ category: slug, listing: undefined }}>
            {t("boards.category.getOnThisBoard")}
          </Link>
        </Button>
      </div>

      {children.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-2">
          {children.map((child) => (
            <Link
              key={child.id}
              to="/c/$slug"
              params={{ slug: child.slug }}
              className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
            >
              {child.name}
            </Link>
          ))}
        </div>
      )}

      <div className="mt-8 space-y-3">
        {isLoading && (
          <div className="card-surface p-6 text-sm text-muted-foreground">{t("boards.category.loadingBoard")}</div>
        )}
        {!isLoading && board.length === 0 && (
          <>
            <EmptyBoard categorySlug={slug} />
            <MockBoardList seedKey={slug} categorySlug={slug} />
          </>
        )}
        {board.map((row, i) => (
          <BoardEntry key={row.listing.id} row={row} rank={i + 1} categorySlug={slug} />
        ))}
      </div>

      {discoverOnly.length > 0 && (
        <div className="mt-10">
          <h2 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">
            {t("boards.category.discoverMore")}
          </h2>
          <div className="mt-3 grid grid-cols-[minmax(0,1fr)] gap-3 sm:grid-cols-[repeat(2,minmax(0,1fr))]">
            {discoverOnly.map((l) => (
              <DiscoverEntry key={l.id} listing={l} />
            ))}
          </div>
        </div>
      )}

      <BoardHistoryLinks boardSlug={slug} />
    </div>
  );
}
